import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';

const InstallPrompt = lazy(() => import('./components/shared/pwa/InstallPrompt').then(m => ({ default: m.InstallPrompt })));
const PWAInstallBanner = lazy(() => import('./components/shared/pwa/PWAInstallBanner').then(m => ({ default: m.PWAInstallBanner })));
const OfflineIndicator = lazy(() => import('./components/shared/pwa/OfflineIndicator').then(m => ({ default: m.OfflineIndicator })));
const PWAStatus = lazy(() => import('./components/shared/pwa/PWAStatus').then(m => ({ default: m.PWAStatus })));
const PWAUpdateNotification = lazy(() => import('./components/shared/pwa/PWAUpdateNotification').then(m => ({ default: m.PWAUpdateNotification })));
const UpdatePrompt = lazy(() => import('./components/shared/pwa/UpdatePrompt').then(m => ({ default: m.UpdatePrompt })));
const PlatformDisclaimer = lazy(() => import('./components/shared/PlatformDisclaimer').then(m => ({ default: m.PlatformDisclaimer })));
const EnhancedGDPRBanner = lazy(() => import('./components/shared/EnhancedGDPRBanner').then(m => ({ default: m.EnhancedGDPRBanner })));
const QuickActionsWidget = lazy(() => import('./components/pwa/QuickActionsWidget').then(m => ({ default: m.QuickActionsWidget })));

import { Home } from './pages/Home';
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Converter = lazy(() => import('./pages/Converter').then(m => ({ default: m.Converter })));
const Education = lazy(() => import('./pages/Education').then(m => ({ default: m.Education })));
const EducationHub = lazy(() => import('./pages/EducationHub').then(m => ({ default: m.EducationHub })));
const NairaJourney = lazy(() => import('./pages/NairaJourney').then(m => ({ default: m.NairaJourney })));
const Terms = lazy(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/legal/Privacy').then(m => ({ default: m.Privacy })));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer').then(m => ({ default: m.Disclaimer })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(m => ({ default: m.BlogPost })));
const SignIn = lazy(() => import('./pages/SignIn').then(m => ({ default: m.SignIn })));
const SignUp = lazy(() => import('./pages/SignUp').then(m => ({ default: m.SignUp })));
const UserDashboard = lazy(() => import('./components/user/UserDashboard').then(m => ({ default: m.UserDashboard })));
const AuthGuard = lazy(() => import('./components/auth/AuthGuard').then(m => ({ default: m.AuthGuard })));
const AutoContentGenerator = lazy(() => import('./components/blog/AutoContentGenerator').then(m => ({ default: m.AutoContentGenerator })));
const AdminLogin = lazy(() => import('./pages/admin/Login').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard').then(m => ({ default: m.AdminDashboard })));
const AdminPasswordReset = lazy(() => import('./pages/admin/PasswordReset').then(m => ({ default: m.AdminPasswordReset })));
import { MarketTicker } from './components/shared/MarketTicker';
import { usePWA } from './hooks/usePWA';
import { initializeGA } from './utils/analytics/gtag';
import { useAnalytics } from './hooks/useAnalytics';
import { EnhancedErrorBoundary } from './utils/security/error-boundary';
import { useOfflineSupport } from './hooks/useOfflineSupport';
import { LoadingSpinner } from './components/shared/LoadingSpinner';

const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-16">
    <LoadingSpinner />
  </div>
);

const RouteErrorBoundary = ({ children }: { children: React.ReactNode }) => (
  <EnhancedErrorBoundary>
    {children}
  </EnhancedErrorBoundary>
);

function App() {
  const { needRefresh, updateServiceWorker, closePrompt } = usePWA();
  const { isOnline, hasOfflineData } = useOfflineSupport();

  useEffect(() => {
    initializeGA();
  }, []);

  useAnalytics();

  return (
    <EnhancedErrorBoundary>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <MarketTicker />
        <main className="flex-grow">
          <RouteErrorBoundary>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/converter" element={<Converter />} />
              <Route path="/education" element={<Education />} />
              <Route path="/education-hub" element={<EducationHub />} />
              <Route path="/education/path/:pathId" element={<EducationHub />} />
              <Route path="/education/module/:moduleId" element={<EducationHub />} />
              <Route path="/education/*" element={<Home />} />
              <Route path="/naira-journey" element={<NairaJourney />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/blog/category/:category" element={<Blog />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route
                path="/dashboard"
                element={
                  <AuthGuard>
                    <UserDashboard />
                  </AuthGuard>
                }
              />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<AdminPasswordReset />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
            </Routes>
          </Suspense>
          </RouteErrorBoundary>
        </main>
        <Footer />
        <Suspense fallback={null}>
          <InstallPrompt />
          <PWAInstallBanner />
          <PlatformDisclaimer />
          <EnhancedGDPRBanner />
          <QuickActionsWidget />
          <OfflineIndicator isOnline={isOnline} hasOfflineData={hasOfflineData} />
          <PWAStatus />
          <PWAUpdateNotification />
          {needRefresh && (
            <UpdatePrompt
              onUpdate={() => updateServiceWorker?.()}
              onDismiss={closePrompt}
            />
          )}
        </Suspense>
      </div>
    </EnhancedErrorBoundary>
  );
}

export default App;
